// The one owner of sound in the client: the manifest, the WebAudio graph,
// the music transport, the SFX voices and the spoken lines that ride with
// them. Everything it decides — how loud, how far left, which variant,
// which track next, when a voice line starts — comes out of `model.ts`, so
// this file is glue and state and nothing else.
//
// Two rules shape it. Audio is optional: no manifest means the director is
// unavailable and every call is a no-op, so a host with nothing published
// boots clean. And audio is gesture-gated: browsers refuse to start an
// AudioContext before the player touches the page, and the direct/dev entry
// mode has no landing screen to take that gesture on, so `unlock()` installs
// its own listeners, and they stay armed until the context reports that it
// is running (R631, audio.md §8.2). A cue that arrives before the
// unlock is dropped, never queued — a burst of stale explosions on first
// click would be worse than silence. The music MODE is the one override on
// top of both:
// while a mode is on the playlist comes from the manifest's own entry for
// that screen instead of the stored setting, and nothing here writes that
// setting back (audio.md §3.6, §3.7). There are two — the menu and an
// arcade match — and the arcade falls back on the menu's entry.
// And the music survives a page change: the director saves its playlist,
// track and position when the page hides, and the director on the next page
// picks that record up at boot (`handoff.ts`, R320). The restore applies
// only where the new page selected the same playlist by itself, so nothing
// here takes the playlist decision away from §3.6.
// The music transport is TWO decks and not one (audio.md §3.9, R393). Each
// deck is an element with its own gain, both gains feed the music bus, and
// a track change crosses the two of them over `crossfadeSec` seconds. One
// deck sounds at a time, except across a fade, where two do.

import {
  bareUrl, parseManifest, pickPlaylist, pickTheme,
  type AudioManifest, type MusicPlaylist, type MusicTrack, type SfxTheme,
} from './manifest';
import { readHandoff, writeHandoff } from './handoff';
import {
  crossfadeCurve, makeShuffle, nextIndex, panOf, pickVariant, positionalGain, prevIndex,
  type ViewRect, type VoiceMode, voiceLinesFor, voiceStartOffset,
} from './model';
import { LOCALE } from '../gen/strings';
import type { SfxId } from './triggers';
// The voice cap, the audible floor and the crossfade window ride
// presentation.toml [presentation.audio] (R312). SFX_MUTED rides the cue
// table data/presentation/sfx.toml (audio.md §4.6).
import {
  AUDIO_CLOCK_DRIFT_MAX, AUDIO_CLOCK_WATCH_SEC, AUDIO_CROSSFADE_SEC, AUDIO_HIDE_FADE_SEC,
  AUDIO_MAX_VOICES, AUDIO_MIN_AUDIBLE_GAIN, SFX_MUTED,
} from '../gen/presentation';
import { clamp01 } from '../render/math';

/** Where the publish script puts the catalogue. Same-origin: the prod CSP
 *  is `media-src 'self'`, and nginx aliases `/audio/` to the media dir. */
const MANIFEST_URL = '/audio/manifest.json';

/** How many SFX may sound at once. A siege can throw dozens of hits into
 *  one tick; past this they are mud, so a new voice is dropped rather than
 *  stealing an older one — the older one is the one already audible. */
const MAX_VOICES = AUDIO_MAX_VOICES;

/** Below this a map cue is inaudible, so it is never fetched. */
const MIN_AUDIBLE_GAIN = AUDIO_MIN_AUDIBLE_GAIN;

/** How long one track crosses into the next (audio.md §3.9). 0 turns the
 *  crossfade off, and every track change is a hard cut. */
const CROSSFADE_SEC = AUDIO_CROSSFADE_SEC;

/** The time constant of the master fade at a hide and a show (§8.3). */
const HIDE_FADE_SEC = AUDIO_HIDE_FADE_SEC;

/** The window of the clock watch, and the rate error it allows (§8.8). */
const CLOCK_WATCH_SEC = AUDIO_CLOCK_WATCH_SEC;
const CLOCK_DRIFT_MAX = AUDIO_CLOCK_DRIFT_MAX;

/** The media query of a phone or a tablet: there a hidden page is an app in
 *  the background, and iOS stops its sound with a click (§8.3). */
const TOUCH_DEVICE_QUERY = '(hover: none) and (pointer: coarse)';

function touchDevice(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia(TOUCH_DEVICE_QUERY).matches;
  } catch {
    return false;
  }
}

/** What the unlock listens on (R631). The press events are what a desktop
 *  browser grants the activation on, and the release events are what WebKit
 *  grants it on: a touch `pointerdown` on iOS carries no activation at all,
 *  so a listener on the press alone never unlocks a phone. */
const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'];

/** A key that grants no activation of its own, so it must not spend the
 *  unlock: the player is holding a modifier for the press that follows. */
const BARE_MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'Meta']);

/**
 * Names the audio session (R631, R695). Safari 17+ carries
 * `navigator.audioSession`; every other browser carries nothing, and there
 * this is a no-op.
 *
 * Two types matter. `ambient` mixes with the app that plays already, and
 * the ring/silent switch mutes the game. `playback` declares the page a
 * music app: iOS stops the other app and ignores the ring/silent switch.
 * The player picks between the two with the `audioTakeover` switch, and
 * `ambient` is the default.
 */
function audioSession(type: string): void {
  const n = navigator as { audioSession?: { type: string } };
  if (!n.audioSession) return;
  try {
    n.audioSession.type = type;
  } catch { /* a browser that refuses the write keeps its default */ }
}

/** The line that holds the one spoken channel. `src` is set once the
 *  buffers arrived; `cut` marks a line a priority line took the channel
 *  from before its fetch landed, so it never starts. */
interface VoiceLine {
  until: number;
  src: AudioBufferSourceNode | null;
  cut: boolean;
}

/** What the director may be built with. The crossfade window defaults to
 *  the generated figure; a test injects its own. */
export interface DirectorOptions {
  crossfadeSec?: number;
  /** The language the voice lines speak (`audio.md` §5.4). The picked
   *  language of the page when absent. */
  locale?: string;
}

/**
 * One of the two music decks (audio.md §3.9). A deck is an element, its own
 * gain on the music bus, and the little state the director keeps about it.
 */
interface Deck {
  el: HTMLAudioElement;
  /** The deck's own gain. The crossfade moves this one; `musicVol` moves
   *  the shared parent, so a volume change during a fade still works. */
  gain: GainNode;
  source: MediaElementAudioSourceNode | null;
  /** The url the deck holds, or '' when it is unloaded. A cleared `el.src`
   *  reads back as the document url, so the director keeps this itself. */
  url: string;
  /** True from the moment the director asked the element to play until it
   *  paused, stopped or ended. This is the director's own intent, which is
   *  what the transport decisions need — `el.paused` lags the play promise. */
  live: boolean;
  /** True once a gesture warmed this element with a play/pause pair (R631).
   *  iOS unlocks a media element on its first play inside a gesture, and the
   *  crossfade's own first play is far outside one. */
  primed: boolean;
  /** True once a refused play was reported, so one dead deck writes one
   *  console line and not one per track. */
  reported: boolean;
  /** The armed handoff seek, so the next load can unhook it. */
  seek: (() => void) | null;
  /** Drops the deck's element listeners at teardown. */
  off: () => void;
}

/** The audio half of GameSettings (plan §5). '' selects the manifest
 *  default; an id the manifest does not carry degrades to it as well. */
export interface AudioSettings {
  musicOn: boolean;
  sfxOn: boolean;
  /** True where the player lets the game stop the sound of another app
   *  (R695). It picks the audio session type and nothing else. */
  audioTakeover: boolean;
  musicVol: number;
  sfxVol: number;
  playlist: string;
  sfxTheme: string;
  /**
   * A custom Music theme (themes.md §3.2, R814): manifest track URLs in
   * play order. While one is set and no mode is on, it plays instead of
   * `playlist`. A URL the loaded manifest does not hold is skipped, and a
   * list with no known track falls back to `playlist`.
   */
  customPlaylist?: CustomPlaylist | null;
  /** A custom SFX theme (themes.md §3.3). It sounds instead of `sfxTheme`.
   *  A muted id stays muted: `SFX_MUTED` is tested before any theme. */
  customSfx?: SfxTheme | null;
  /** The entitlement keys the account holds. A keyed playlist or SFX theme
   *  the set does not hold is passed over (themes.md §4.2). Absent is none. */
  held?: ReadonlySet<string>;
}

/** A custom Music theme as the director takes it. `id` is stable for one
 *  theme code, so the handoff finds it again on the next page. */
export interface CustomPlaylist {
  id: string;
  title: string;
  tracks: string[];
  shuffle: boolean;
}

const NO_KEYS: ReadonlySet<string> = new Set();

/** One playlist or SFX theme as a picker lists it. `key` is the tier mark. */
export interface RosterEntry { id: string; title: string; key?: string }

/** The picker's view of a manifest list: id, title and the tier mark where
 *  one is set. */
function roster(list: readonly RosterEntry[]): RosterEntry[] {
  return list.map((e) => ({ id: e.id, title: e.title, ...(e.key ? { key: e.key } : {}) }));
}

/** One custom playlist as a comparable text. */
function customKey(c: CustomPlaylist | null | undefined): string {
  return c ? `${c.id}\n${c.shuffle}\n${c.tracks.join('\n')}` : '';
}

/** One entitlement set as a comparable text. */
function heldKey(h: ReadonlySet<string> | undefined): string {
  return h ? [...h].sort().join(',') : '';
}

const DEFAULTS: AudioSettings = {
  musicOn: true,
  sfxOn: true,
  audioTakeover: false,
  musicVol: 0.6,
  sfxVol: 0.8,
  playlist: '',
  sfxTheme: '',
};

/** What the media-player card renders. Null when nothing is playable. */
export interface MusicStatus {
  playlist: string;
  playlistTitle: string;
  /** Index into `tracks`, not into the shuffled play order. */
  trackIndex: number;
  title: string;
  /** Seconds. `duration` falls back to the manifest's probe until the
   *  element has enough of the file to report its own. */
  time: number;
  duration: number;
  playing: boolean;
  shuffle: boolean;
  tracks: { title: string }[];
}

/**
 * A screen that plays a playlist of the manifest's naming rather than the
 * player's own (audio.md §3.6, §3.7).
 *
 * `'menu'` is the landing screen, the hub and the arcade lobby.
 * `'arcade'` is a match in progress. Null is the game: the stored
 * `playlist` setting selects, and the media-player card is drawn.
 */
export type MusicMode = 'menu' | 'arcade' | null;

type Ctor = new () => AudioContext;

function audioContextCtor(): Ctor | null {
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function clampVol(v: number): number {
  return typeof v === 'number' && isFinite(v) ? clamp01(v) : 0;
}

/**
 * Reads the manifest. Null for anything short of a manifest-shaped JSON
 * document: a 404 on a host with nothing published, an offline fetch, and
 * equally a dev server answering 200 with `index.html` for a path it does
 * not know (the same trap `net/api.ts` guards). The director boots from
 * it, and the theme workshop reads its bank through it.
 */
export async function fetchManifest(): Promise<AudioManifest | null> {
  let res: Response;
  try {
    res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
  } catch {
    return null; // no audio published, or offline: the client runs silent
  }
  if (!res.ok) return null;
  const type = res.headers.get('content-type') ?? '';
  if (type && !type.includes('json')) return null;
  const text = await res.text();
  if (text.trimStart().startsWith('<')) return null; // a dev server's index.html
  try {
    return parseManifest(JSON.parse(text));
  } catch {
    return null;
  }
}

export class AudioDirector {
  private manifest: AudioManifest | null = null;
  private settings: AudioSettings = { ...DEFAULTS };

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  // Two decks for the whole session: a MediaElementAudioSourceNode binds to
  // its element for good, so a deck changes track by swapping `.src`.
  // `cur` is the deck that is audibly current, which across a fade is the
  // one fading IN (§3.9).
  private decks: Deck[] = [];
  private cur = 0;
  /** The deck fading out, while a crossfade is in flight. Null otherwise. */
  private fadeFrom: Deck | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  /** The crossfade window in seconds. 0 is a hard cut on one deck. */
  private readonly crossfadeSec: number;

  private playlist: MusicPlaylist | null = null;
  private theme: SfxTheme | null = null;
  /** Play order over the current playlist: identity, or a shuffle of it. */
  private order: number[] = [];
  private pos = 0;
  private shuffle = false;
  /** True after musicStop, until the player asks for music again. Keeps a
   *  settings re-apply or an unlock from restarting what was stopped. */
  private stopped = false;
  /** True after the player pressed PAUSE or STOP on the media card, until
   *  they ask for music again. It rides the handoff (R396), so the next
   *  page can tell a deliberate pause from a page that was never
   *  unlocked — only the first must hold that page's boot start. */
  private pausedByUser = false;
  /** The music mode: while one is on, the playlist comes from the manifest
   *  and not from the stored setting, and the screen shows no transport
   *  for it (§3.6, §3.7). Null is the game, which plays the stored
   *  choice. */
  private mode: MusicMode = null;

  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private lastVariant = new Map<SfxId, number>();
  private lastVoiceVariant = new Map<SfxId, number>();
  /** The language of the voice lines. `spawn` reads it on every cue, so a
   *  change takes effect on the next one (R1145). */
  private locale: string = LOCALE;
  private voices = 0;
  /** The voice line that holds the one spoken channel, and the context time
   *  it is done at. A fetch in flight holds it with an infinite time, so two
   *  cues in one tick cannot both take it. Null means the channel is free. */
  private voiceLine: VoiceLine | null = null;
  /** The hush of the idle sea view (R2977, audio.md §8.1): no effect and
   *  no spoken line starts. The music bus and the stored settings stay. */
  private hushed = false;

  private view: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private zoom = 1;
  private viewX = 0;
  private screenW = 1;

  private unlockHandler: ((ev: Event) => void) | null = null;
  private dead = false;
  /** True only while `applySettings` holds the context suspended for two off
   *  switches. Every other suspension is the browser's, and that one is
   *  what the recovery paths below may resume from (R631). */
  private ownSuspend = false;
  /** True from the moment the context reported `interrupted`, or a
   *  suspension the director did not ask for, until the music is back. iOS
   *  pauses the element under it and reports nothing to the transport. */
  private wasInterrupted = false;
  /** True while the master gain fades to 0 or sits there: the page left, or
   *  a phone put it in the background (§8.3). */
  private faded = false;
  /** One count per graph. A source of a graph that `renew` closed must not
   *  give a slot back to the cap of the next graph (§8.8). */
  private epoch = 0;
  /** The clock watch (§8.8): its timer, the last pair of readings, and the
   *  number of windows in a row whose rate was out of bounds. */
  private watchTimer: ReturnType<typeof setInterval> | null = null;
  private watchMark: { audio: number; wall: number } | null = null;
  private watchFaults = 0;

  /** Seconds to seek to as soon as the next track has its metadata. It
   *  carries a handoff's position into the element, which cannot be seeked
   *  before it knows the duration of the file. */
  private pendingSeek = 0;

  private onPageHide = () => {
    this.saveHandoff();
    // The page leaves: the sound fades out before the browser cuts it.
    this.hideFade(true);
  };

  /** The context changed state under the director (R631). iOS reports the
   *  end of an interruption here, and it is also where WebKit reports the
   *  `running` a gesture asked for, long after the gesture task ended. */
  private onStateChange = () => {
    const ctx = this.ctx;
    if (!ctx || this.dead) return;
    // `interrupted` is iOS only and is not in the standard union.
    const state: string = ctx.state;
    console.info('[audio] state', state);
    if (state === 'running') {
      this.clearUnlock();
      this.restartIfInterrupted();
      return;
    }
    if (state === 'interrupted' || (state === 'suspended' && !this.ownSuspend)) {
      this.wasInterrupted = true;
      // The next touch is what resumes it, where a bare resume is refused.
      this.unlock();
    }
  };

  private onVisibility = () => {
    if (document.visibilityState === 'visible') {
      this.hideFade(false);
      this.tryResume('visible');
      return;
    }
    // A desktop tab in the background keeps its music. On a phone the page
    // is an app in the background, and iOS stops the sound at full level
    // with a click and a buzz, so the master fades out first (§8.3).
    if (touchDevice()) this.hideFade(true);
  };

  private onPageShow = () => {
    this.hideFade(false);
    this.tryResume('pageshow');
  };

  private onFocus = () => { this.tryResume('focus'); };

  constructor(opts: DirectorOptions = {}) {
    const s = opts.crossfadeSec ?? CROSSFADE_SEC;
    this.crossfadeSec = typeof s === 'number' && isFinite(s) && s > 0 ? s : 0;
    if (opts.locale) this.locale = opts.locale;
    // `pagehide` and not `unload`: it fires on a same-tab navigation and on
    // the back/forward cache, which is every way out of this page.
    window.addEventListener('pagehide', this.onPageHide);
    // The three ways back from an interruption (R631): the tab is looked at
    // again, the page comes out of the back/forward cache, and the window
    // takes the focus back. On iOS a phone call, Siri, the lock screen and
    // an app switch all leave the context stopped, and none of the three is
    // a gesture, so `tryResume` re-arms the unlock as well.
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('focus', this.onFocus);
  }

  /** Writes where the transport stands, for the page that comes next. */
  private saveHandoff(): void {
    if (!this.playlist) return;
    // Across a fade the record names the deck that is audibly current, so
    // the next page resumes the track the player is hearing (§3.9).
    const d = this.deck();
    writeHandoff({
      playlist: this.playlist.id,
      track: this.order[this.pos] ?? 0,
      time: this.stopped ? 0 : (d?.el.currentTime ?? 0),
      playing: !this.stopped && !!d && d.live,
      paused: this.pausedByUser,
      shuffle: this.shuffle,
    });
  }

  /**
   * Takes the record the last page left, where it fits (R320).
   *
   * It fits only when the playlist this page selected on its own carries the
   * same id and still holds the track. The handoff moves the cursor inside a
   * playlist; it never picks the playlist. That keeps §3.6's selection rules
   * — the stored setting, the menu override, the manifest default — the one
   * authority on what plays.
   *
   * Answers true when a record fit and says the player had paused the
   * music, so the boot must leave the transport still. A record that only
   * says "nothing was playing" does not hold it: that is a page nobody
   * had unlocked yet, not a choice (R396).
   */
  private applyHandoff(): boolean {
    const h = readHandoff();
    const p = this.playlist;
    if (!h || !p || h.playlist !== p.id || h.track >= p.tracks.length) return false;
    this.shuffle = h.shuffle;
    this.order = this.makeOrder(p.tracks.length, h.track);
    const at = this.order.indexOf(h.track);
    if (at < 0) return false;
    this.pos = at;
    this.pendingSeek = h.time;
    this.pausedByUser = h.paused;
    return h.paused;
  }

  /**
   * Starts the music with no gesture of this page's own, where the browser
   * allows that. Chrome allows it on a page reached from a same-origin page
   * by a navigation that carried the player's activation (its autoplay FAQ:
   * the gesture requirement "applies to contiguous navigations" — the walk
   * hub ⇄ arcade, and `ui/nav.ts` is what attaches the gesture), and on a
   * site whose media engagement has earned it. A browser that refuses
   * leaves the resume promise pending until the armed `unlock()` gesture
   * fires, and then this resolves as well — `musicPlay` twice is
   * idempotent. A fresh browser-initiated load (a typed address, a reload,
   * the back button) never qualifies, and there the first press starts it.
   */
  private tryAutostart(): void {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    // Through `afterResume`, so a browser that reports the state change on
    // the promise and not on a `statechange` event still drops the
    // listeners the unlock armed.
    void ctx.resume()
      .then(() => { this.afterResume(); })
      .catch(() => { /* no autoplay: the first press starts it */ });
  }

  /**
   * Reads the manifest through `fetchManifest`. Anything short of a
   * manifest leaves the director unavailable.
   */
  async init(): Promise<void> {
    const manifest = await fetchManifest();
    if (this.dead || !manifest) return;
    this.manifest = manifest;
    this.selectFromSettings();
    const paused = this.applyHandoff();
    // Every boot tries to start the music (R396) — not only one that took
    // a handoff. This is what plays the track where the browser already
    // allows it: a navigation that carried the gesture, an earned
    // engagement score, or an unlock that fired before this fetch
    // settled. Only a handoff that says "the player paused" holds it.
    if (this.musicWanted() && !paused) this.tryAutostart();
  }

  /** True once a manifest with something in it loaded. The settings rows
   *  and the media-player card are hidden until it is. */
  get available(): boolean {
    return this.manifest !== null
      && (this.manifest.music.playlists.length > 0 || this.manifest.sfx.themes.length > 0);
  }

  /** The published playlists, for the pickers. `key` is the tier mark. */
  playlists(): RosterEntry[] {
    return roster(this.manifest?.music.playlists ?? []);
  }

  /** The published SFX themes, with their tier marks. */
  themes(): RosterEntry[] {
    return roster(this.manifest?.sfx.themes ?? []);
  }

  /**
   * Arms the gesture unlock (R631, audio.md §8.2). The listeners
   * are capture-phase on `window`, so they see the gesture whatever the
   * canvas or the overlay does with it, and they cover the press events and
   * the release events both: WebKit grants the user activation at the
   * release of a touch, and a listener on the press alone never unlocks a
   * phone. They are NOT one-shot. A fire that leaves the context short of
   * `running` must keep them, because the next gesture is the only other
   * chance the page gets; `clearUnlock` runs once the context reports it is
   * running, on the fire itself or later on a `statechange`.
   */
  unlock(): void {
    if (this.unlockHandler || this.dead) return;
    const fire = (ev: Event) => { this.onGesture(ev); };
    this.unlockHandler = fire;
    for (const type of UNLOCK_EVENTS) {
      window.addEventListener(type, fire, { capture: true });
    }
  }

  private clearUnlock(): void {
    const fire = this.unlockHandler;
    if (!fire) return;
    for (const type of UNLOCK_EVENTS) {
      window.removeEventListener(type, fire, { capture: true });
    }
    this.unlockHandler = null;
  }

  /**
   * One gesture, handled synchronously (R631). Everything a browser only
   * allows inside the gesture task happens here and nothing waits on a
   * promise first: the context primer, the play/pause that warms the idle
   * deck, and the current deck's own `play()`. iOS grants the permission to
   * the task and not to the page, so a `play()` after an `await` is refused.
   */
  private onGesture(ev: Event): void {
    if (this.dead) return;
    // A keydown of a bare modifier grants no activation, and spending the
    // unlock on it would leave the context suspended with the listeners gone.
    if (ev.type === 'keydown' && BARE_MODIFIERS.has((ev as KeyboardEvent).key)) return;
    // The type is named on every unlock, music or no music: `ambient` is
    // the correct answer while the music is off as well (R695).
    audioSession(this.sessionType());
    const ctx = this.ensureCtx();
    if (!ctx) {
      // No constructor at all: nothing will ever unlock, so stop listening.
      this.clearUnlock();
      return;
    }
    const before: string = ctx.state;
    this.ownSuspend = false;
    void ctx.resume()
      .then(() => { this.afterResume(); })
      .catch(() => { /* the next gesture tries again */ });
    console.info('[audio] fire', ev.type, before, '->', ctx.state);
    this.prime(ctx);
    this.warmDecks();
    if (this.musicWanted()) this.musicPlay();
    // Chrome reports `running` here; iOS reports it later, on a statechange.
    if (ctx.state === 'running') this.clearUnlock();
  }

  /** What a resume that reached `running` releases: the armed listeners, the
   *  music, and an element iOS paused under an interruption. */
  private afterResume(): void {
    const ctx = this.ctx;
    if (this.dead || !ctx || ctx.state !== 'running') return;
    this.clearUnlock();
    if (this.musicWanted()) this.musicPlay();
    this.restartIfInterrupted();
  }

  /**
   * The classic iOS unlock: one silent one-frame buffer, started inside the
   * gesture task. It drives WebKit's own "a page that began playback" path,
   * which is what takes the context out of its muted state.
   */
  private prime(ctx: AudioContext): void {
    if (typeof ctx.createBuffer !== 'function') return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      // Straight to the destination and not through the buses: this makes no
      // sound, and the music switch must not be able to hold it back.
      src.connect(ctx.destination);
      src.start(0);
    } catch { /* a graph that refuses the primer still plays music */ }
  }

  /**
   * Warms every idle deck with a play/pause pair inside the gesture (R631).
   * iOS unlocks a media element on its first play in a gesture task, and the
   * idle deck's own first play is the crossfade, seconds or minutes later.
   * A deck that holds a track is left alone: it was paused on purpose.
   */
  private warmDecks(): void {
    for (const d of this.decks) {
      if (d.url || d.live || d.primed) continue;
      try {
        const p = d.el.play();
        if (p && typeof p.catch === 'function') p.catch(() => { /* stays cold */ });
        d.el.pause();
      } catch { /* no implementation: nothing to warm */ }
      d.primed = true;
    }
  }

  /**
   * Tries to bring a stopped context back with no gesture (R631). WebKit
   * allows this once the context was unlocked and the interruption is over,
   * and refuses it while the interruption holds — so the unlock is re-armed
   * first, and the player's next touch is the fallback.
   */
  private tryResume(trigger: string): void {
    const ctx = this.ctx;
    if (!ctx || this.dead || this.ownSuspend || ctx.state === 'running') return;
    this.unlock();
    void ctx.resume().then(() => {
      console.info('[audio] resume', trigger, ctx.state);
      this.afterResume();
    }).catch(() => { /* the armed gesture is the other path */ });
  }

  /**
   * Restarts the music an interruption stopped. iOS pauses the element while
   * the director's own `live` flag stays true, so nothing else notices that
   * the music ended. The flag gate, and not `el.paused` alone, is what keeps
   * a plain first unlock from playing the deck twice, and `pausedByUser`
   * keeps a pause the player asked for.
   */
  private restartIfInterrupted(): void {
    if (!this.wasInterrupted) return;
    this.wasInterrupted = false;
    const d = this.deck();
    if (!d || !this.musicWanted() || this.pausedByUser) return;
    if (!d.url || !d.el.paused) return;
    d.live = false;
    this.start(d);
  }

  /**
   * Fades the master gain to 0, or back to 1, on the context clock (§8.3).
   * The fade runs on the audio thread, so it ends even when the page gets
   * no more main-thread time after the hide. Each target starts from the
   * value the last one reached, so a hide and a show close together give
   * no jump.
   */
  private hideFade(on: boolean): void {
    if (on === this.faded) return;
    this.faded = on;
    const ctx = this.ctx;
    const p = this.master?.gain;
    if (!ctx || !p) return;
    const v = on ? 0 : 1;
    if (typeof p.setTargetAtTime === 'function') {
      try {
        p.setTargetAtTime(v, ctx.currentTime, HIDE_FADE_SEC);
        return;
      } catch { /* no automation: the value is set at once */ }
    }
    p.value = v;
  }

  /**
   * Starts the clock watch (§8.8). Each window compares the time the audio
   * clock moved with the time the wall clock moved. Firefox under a load
   * spike can leave its output in a state that warps every sound until a
   * reload, and the context clock then runs at a wrong rate. Two windows
   * in a row out of bounds renew the graph; one window alone is a stall
   * that ends by itself, or a sleep of the machine.
   */
  private watchClock(): void {
    if (this.watchTimer !== null || CLOCK_WATCH_SEC <= 0) return;
    this.watchTimer = setInterval(() => { this.checkClock(); }, CLOCK_WATCH_SEC * 1000);
  }

  private stopWatch(): void {
    if (this.watchTimer !== null) clearInterval(this.watchTimer);
    this.watchTimer = null;
    this.watchMark = null;
    this.watchFaults = 0;
  }

  /** One window of the clock watch. Public for the tests alone. */
  checkClock(): void {
    const ctx = this.ctx;
    // A clock that does not run has no rate: a suspension, an interruption
    // or the hide fade start the count again.
    if (!ctx || this.dead || ctx.state !== 'running' || this.faded) {
      this.watchMark = null;
      this.watchFaults = 0;
      return;
    }
    const mark = { audio: ctx.currentTime, wall: performance.now() / 1000 };
    const last = this.watchMark;
    this.watchMark = mark;
    if (!last) return;
    const wall = mark.wall - last.wall;
    // A throttled timer gives a long window, and that is a good one; a
    // window shorter than half the period is too short to read a rate.
    if (wall < CLOCK_WATCH_SEC / 2) return;
    const rate = (mark.audio - last.audio) / wall;
    if (Math.abs(rate - 1) <= CLOCK_DRIFT_MAX) {
      this.watchFaults = 0;
      return;
    }
    this.watchFaults++;
    console.info('[audio] clock', rate.toFixed(3), 'fault', this.watchFaults);
    if (this.watchFaults >= 2) this.renew();
  }

  /**
   * Closes the graph and builds a new one, and the music continues at the
   * track and the second it held (§8.8). A new context opens a new output
   * stream, which is what a reload does to a warped output. A media element
   * stays bound to the source node of its old context, so the decks are new
   * elements too. The decoded effect clips are not bound to a context, and
   * the bank keeps them.
   */
  private renew(): void {
    if (!this.ctx || this.dead) return;
    console.info('[audio] renew');
    this.settleFade();
    const d = this.deck();
    const url = d?.url ?? '';
    const time = url && d ? d.el.currentTime : 0;
    const playing = !!d && d.live && url !== '';
    this.closeGraph();
    const ctx = this.ensureCtx();
    if (!ctx) return;
    const next = this.deck();
    if (url && next) {
      this.pendingSeek = time;
      this.loadInto(next, playing);
    }
    // The page holds the activation of the gesture that unlocked it, so the
    // resume needs no new touch. A browser that refuses it waits for one.
    void ctx.resume().then(() => {
      if (ctx.state === 'running') this.clearUnlock();
    }).catch(() => { /* the armed gesture is the other path */ });
    if (ctx.state !== 'running') this.unlock();
  }

  /** Stops every deck and closes the context. `destroy` and `renew` both
   *  end the graph here. */
  private closeGraph(): void {
    this.stopWatch();
    this.clearFadeTimer();
    this.fadeFrom = null;
    for (const d of this.decks) {
      d.off();
      d.live = false;
      // A handoff seek that still waits on the metadata goes too (R1362).
      this.unhookSeek(d);
      if (d.url) {
        d.url = '';
        d.el.pause();
        d.el.removeAttribute('src');
      }
      d.source = null;
    }
    this.decks = [];
    this.voiceLine = null;
    this.voices = 0;
    this.epoch++;
    if (this.ctx) {
      if (typeof this.ctx.removeEventListener === 'function') {
        this.ctx.removeEventListener('statechange', this.onStateChange);
      }
      void this.ctx.close().catch(() => { /* already closed */ });
      this.ctx = null;
    }
    this.master = null;
    this.musicGain = null;
    this.sfxGain = null;
  }

  private ensureCtx(): AudioContext | null {
    if (this.ctx || this.dead) return this.ctx;
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
    this.ctx = ctx;
    // iOS reports the end of an interruption, and the late arrival of the
    // `running` a gesture asked for, on this event alone (R631).
    if (typeof ctx.addEventListener === 'function') {
      ctx.addEventListener('statechange', this.onStateChange);
    }
    console.info('[audio] context', ctx.state);
    this.master = ctx.createGain();
    // 1 while the page shows, 0 under the hide fade (§8.1, §8.3).
    this.master.gain.value = this.faded ? 0 : 1;
    this.master.connect(ctx.destination);
    this.musicGain = ctx.createGain();
    this.musicGain.connect(this.master);
    this.sfxGain = ctx.createGain();
    this.sfxGain.connect(this.master);
    this.applyGains();

    this.decks = [this.makeDeck(ctx), this.makeDeck(ctx)];
    this.cur = 0;
    this.setGain(this.decks[0], 1);
    this.watchClock();
    return ctx;
  }

  /** Builds one deck and hangs it off the music bus. */
  private makeDeck(ctx: AudioContext): Deck {
    const el = new Audio();
    el.preload = 'none';
    const gain = ctx.createGain();
    gain.gain.value = 0;
    if (this.musicGain) gain.connect(this.musicGain);
    const d: Deck = {
      el,
      gain,
      source: null,
      url: '',
      live: false,
      primed: false,
      reported: false,
      seek: null,
      off: () => { /* set below */ },
    };
    const onEnded = () => {
      // A deck that ended is silent already, so there is nothing to fade
      // from: the `ended` path is a hard cut (§3.9). It is the fallback for
      // a track the browser reports no duration for, and for one shorter
      // than the window. The outgoing deck of a fade ends unheard, and it
      // must not move the cursor — only the current deck advances.
      d.live = false;
      if (d === this.deck()) this.advance(1);
    };
    const onTime = () => { this.armCrossfade(d); };
    // Same-origin by construction (nginx aliases /audio/), so no CORS mode.
    el.addEventListener('ended', onEnded);
    el.addEventListener('timeupdate', onTime);
    d.off = () => {
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('timeupdate', onTime);
    };
    try {
      d.source = ctx.createMediaElementSource(el);
      d.source.connect(gain);
    } catch { /* no source node: the element still plays, unmixed */ }
    return d;
  }

  /** The deck that is audibly current. Across a fade it is the incoming
   *  one, from the moment the fade starts (§3.9). */
  private deck(): Deck | null {
    return this.decks[this.cur] ?? null;
  }

  /** The music switch is on and no STOP holds the transport: a boot, an
   *  unlock or a mode change may start the music. */
  private musicWanted(): boolean {
    return this.settings.musicOn && !this.stopped;
  }

  /** Which audio session type the player's switch asks for (R695). */
  private sessionType(): string {
    return this.settings.audioTakeover ? 'playback' : 'ambient';
  }

  private applyGains(): void {
    // The master is not set here: the hide fade owns it (§8.3), and this runs on
    // a cadence that would undo a fade.
    if (this.musicGain) {
      this.musicGain.gain.value = this.settings.musicOn ? clampVol(this.settings.musicVol) : 0;
    }
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.settings.sfxOn ? clampVol(this.settings.sfxVol) : 0;
    }
  }

  /**
   * Takes the player's switches. Both off suspends the context: an off
   * switch must skip the work, not only mute it.
   */
  applySettings(s: AudioSettings): void {
    const prev = this.settings;
    this.settings = {
      musicOn: !!s.musicOn,
      sfxOn: !!s.sfxOn,
      audioTakeover: !!s.audioTakeover,
      musicVol: clampVol(s.musicVol),
      sfxVol: clampVol(s.sfxVol),
      playlist: typeof s.playlist === 'string' ? s.playlist : '',
      sfxTheme: typeof s.sfxTheme === 'string' ? s.sfxTheme : '',
      customPlaylist: s.customPlaylist ?? null,
      customSfx: s.customSfx ?? null,
      held: s.held ?? NO_KEYS,
    };
    // The takeover switch acts at once, so a flip needs no reload (R695).
    if (this.settings.audioTakeover !== prev.audioTakeover) {
      audioSession(this.sessionType());
    }
    if (!this.manifest) return;
    // A mode owns the playlist while it is on. The stored choice is kept
    // in `this.settings` all the same, and it takes effect at the release.
    if (this.mode && !this.playlist) this.selectPlaylist(this.modePlaylistId());
    // A new entitlement set can open or close a keyed entry (themes.md §4.2).
    const heldChanged = heldKey(this.settings.held) !== heldKey(prev.held);
    if (!this.mode && (this.settings.playlist !== prev.playlist || !this.playlist || heldChanged
      || customKey(this.settings.customPlaylist) !== customKey(prev.customPlaylist))) {
      this.selectPlaylist(this.settings.playlist);
    }
    if (this.settings.sfxTheme !== prev.sfxTheme || !this.theme || heldChanged
      || (this.settings.customSfx?.id ?? '') !== (prev.customSfx?.id ?? '')) {
      this.theme = this.pickSfx();
      this.lastVariant.clear();
      this.lastVoiceVariant.clear();
    }
    this.applyGains();
    if (!this.settings.musicOn) {
      // The player turned music off. That is immediate, never a fade (§3.9).
      this.pauseNow();
    } else if (!prev.musicOn && !this.stopped && this.ctx && this.ctx.state === 'running') {
      // Only the off-to-on flip may start music here: this runs on a
      // cadence, and an unconditional play would undo the player's pause.
      audioSession(this.sessionType());
      this.musicPlay();
    }
    if (!this.ctx) return;
    // `interrupted` is iOS only and is not in the standard union.
    const state: string = this.ctx.state;
    if (!this.settings.musicOn && !this.settings.sfxOn) {
      // The director's own suspension, which the recovery paths must not
      // undo behind the player's switches (R631).
      this.ownSuspend = true;
      void this.ctx.suspend().catch(() => { /* already gone */ });
    } else if (state === 'suspended' || state === 'interrupted') {
      this.ownSuspend = false;
      this.tryResume('settings');
    }
  }

  /** Per-frame, from the ticker: the values `playAt` reads at spawn. This
   *  is a store and nothing more — no work happens until a cue fires. */
  setListener(view: ViewRect, zoom: number, viewX: number, screenW: number): void {
    this.view = view;
    this.zoom = zoom;
    this.viewX = viewX;
    this.screenW = screenW;
  }

  /** A cue addressed to this player: full volume, centred. */
  play(id: SfxId, scale = 1, voice: VoiceMode = true): void {
    this.spawn(id, scale, 0, scale, voice);
  }

  /**
   * Hushes the effects and the spoken lines (on) or lets them sound again
   * (off), while the idle sea view covers the map (R2977, audio.md §8.1).
   * A cue in the hush is dropped, never queued, as a cue before the unlock
   * is; a cue whose fetch is in flight at the hush never starts, and the
   * line that speaks stops. An effect that sounds already runs to its end.
   * The music and the stored volumes are not touched.
   */
  hushSfx(on: boolean): void {
    this.hushed = on;
    if (on) this.cutVoice();
  }

  /**
   * Seconds until the one spoken channel is free, or 0 where it is free now.
   * A line whose fetch is still in flight reads as Infinity, because its
   * end is not known yet. A caller that must not lose one line reads this
   * and defers that line itself (audio.md §4.8, R1148); the director keeps
   * no queue.
   */
  voiceBusyForSec(): number {
    const line = this.voiceLine;
    const ctx = this.ctx;
    if (!line || !ctx) return 0;
    return Math.max(0, line.until - ctx.currentTime);
  }

  /** Takes the channel from the line that holds it, for a priority line
   *  (R1148). A started source stops and gives its slot back here, because
   *  its `onended` is unhooked; a line still in its fetch is marked, and
   *  it never starts. */
  private cutVoice(): void {
    const line = this.voiceLine;
    if (!line) return;
    this.voiceLine = null;
    line.cut = true;
    const src = line.src;
    if (!src) return;
    src.onended = null;
    try { src.stop(); } catch { /* the source ended already */ }
    this.voices--;
  }

  /** The language the next voice line speaks (`audio.md` §5.4). A line that
   *  plays already runs to its end in the language it started in. */
  setLocale(tag: string): void {
    if (tag) this.locale = tag;
  }

  /**
   * A cue that happened somewhere on the map. Gain and pan are fixed at
   * spawn and never tracked: these clips are 1-6 s, and a torpedo that
   * slides across the stereo field while the player pans reads as a bug.
   */
  playAt(id: SfxId, x: number, y: number, scale = 1, voice: VoiceMode = true): void {
    const gain = positionalGain(x, y, this.view, this.zoom) * scale;
    if (gain <= MIN_AUDIBLE_GAIN) return;
    this.spawn(id, gain, panOf(x, this.viewX, this.zoom, this.screenW), scale, voice);
  }

  /**
   * One cue: the effect, the voice line of the same id, or both.
   *
   * `gain` and `pan` place the effect on the map. `scale` is the caller's own
   * loudness before the positional term, because a voice line is speech: it
   * is centred and it is not made distant, so a line about a base at the far
   * edge of the map is still understood (the effect there still fades).
   *
   * Where the id has both, the two buffers are awaited together and the pair
   * is scheduled off one instant `t0`: the effect at `t0`, the voice line at
   * `t0 + offset` from `voiceStartOffset`. Waiting for both costs a fetch the
   * first time an id sounds and buys an exact gap every time after it, where
   * starting the effect early would put the two at whatever distance the
   * network left between them.
   *
   * `speak` off takes the effect alone. The caller borrowed this id for a
   * happening the words do not describe, so the spoken channel is neither
   * picked nor held (R595). `speak` as `'priority'` cuts the line that
   * speaks and takes the channel at once (R1148).
   */
  private spawn(id: SfxId, gain: number, pan: number, scale = 1, speak: VoiceMode = true): void {
    // The cue table's gate, and the first line on purpose: `play` and `playAt`
    // both pass through here, so one line mutes a cue on every page.
    if (SFX_MUTED.has(id)) return;
    if (!this.settings.sfxOn || !this.theme || this.dead || this.hushed) return;
    const ctx = this.ctx;
    // Before the unlock there is no context and no permission to make one:
    // the cue is dropped, not queued.
    if (!ctx || ctx.state !== 'running' || !this.sfxGain) return;
    if (this.voices >= MAX_VOICES) return;
    const theme = this.theme;
    const urls = theme.map[id];
    // The line of the picked language, else the English one (R1145).
    const vurls = voiceLinesFor(theme.voice, id, this.locale);
    const hasEffect = !!urls && urls.length > 0;
    const hasVoice = speak && !!vurls && vurls.length > 0;
    if (!hasEffect && !hasVoice) return;

    const epick = hasEffect
      ? pickVariant(urls.length, this.lastVariant.get(id) ?? -1, Math.random)
      : -1;
    if (epick >= 0) this.lastVariant.set(id, epick);
    // ONE spoken channel. Two lines at once are not a mix, they are noise, so
    // a line that arrives while another is still speaking is dropped and the
    // effect of that cue plays alone. A priority line is the one exception:
    // it cuts the line that speaks, and nothing is queued (§5.3, R1148).
    const priority = speak === 'priority';
    const speaking = !priority && this.voiceLine !== null
      && ctx.currentTime < this.voiceLine.until;
    const vpick = hasVoice && !speaking
      ? pickVariant(vurls.length, this.lastVoiceVariant.get(id) ?? -1, Math.random)
      : -1;
    if (vpick >= 0) this.lastVoiceVariant.set(id, vpick);
    if (epick < 0 && vpick < 0) return;
    // The channel is held from here, so a second cue in the same tick cannot
    // take it while this fetch is in flight. Every path below releases it.
    if (vpick >= 0 && priority) this.cutVoice();
    const held: VoiceLine | null = vpick >= 0 ? { until: Infinity, src: null, cut: false } : null;
    if (held) this.voiceLine = held;

    // A voice line counts against the cap like an effect: it is a source on
    // the same bus.
    const want = (epick >= 0 ? 1 : 0) + (vpick >= 0 ? 1 : 0);
    this.voices += want;
    const release = () => {
      if (this.voiceLine === held) this.voiceLine = null;
    };
    const effect = epick >= 0 ? this.buffer(urls[epick]) : Promise.resolve(null);
    const voice = vpick >= 0 && vurls ? this.buffer(vurls[vpick]) : Promise.resolve(null);
    void Promise.all([effect, voice]).then(([ebuf, vbuf]) => {
      const c = this.ctx;
      if (this.dead || this.hushed || !c || c.state !== 'running' || !this.sfxGain) {
        this.voices -= want;
        release();
        return;
      }
      const out = this.sfxGain;
      const t0 = c.currentTime;
      let started = 0;
      if (ebuf) {
        this.source(c, out, ebuf, Math.min(1, gain), pan).start(t0);
        started++;
      }
      if (vbuf && !held?.cut) {
        const offset = voiceStartOffset(theme.voice, id, ebuf ? ebuf.duration : null);
        // A voice line is centred and carries no positional loss: speech is
        // information, not scenery.
        const src = this.source(c, out, vbuf, Math.min(1, scale), 0);
        const free = src.onended;
        src.onended = (ev) => { free?.call(src, ev); release(); };
        src.start(t0 + offset);
        if (held) {
          held.until = t0 + offset + vbuf.duration;
          held.src = src;
        }
        started++;
      } else {
        release();
      }
      // A buffer that never arrived frees the slot it reserved.
      this.voices -= want - started;
    }).catch(() => {
      this.voices -= want;
      release();
    });
  }

  /**
   * One source on the effects bus, at this gain and this stereo placement,
   * ready to be started. It gives its slot in the cap back when it ends, and
   * a caller with more to do at that moment replaces `onended`.
   */
  private source(
    ctx: AudioContext,
    out: GainNode,
    buf: AudioBuffer,
    gain: number,
    pan: number,
  ): AudioBufferSourceNode {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    // Safari before 14.1 has no StereoPannerNode; there the cue plays
    // centred rather than not at all.
    if (pan !== 0 && typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      p.connect(out);
    } else {
      g.connect(out);
    }
    // A source of a graph that `renew` closed gives no slot back (§8.8).
    const epoch = this.epoch;
    src.onended = () => { if (epoch === this.epoch) this.voices--; };
    return src;
  }

  /** One decoded clip per url, fetched once and kept. The bank is small and
   *  every id repeats, so nothing here is ever evicted. */
  private buffer(url: string): Promise<AudioBuffer | null> {
    const held = this.buffers.get(url);
    if (held) return held;
    const p = (async (): Promise<AudioBuffer | null> => {
      const ctx = this.ctx;
      if (!ctx) return null;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return await ctx.decodeAudioData(await res.arrayBuffer());
      } catch {
        return null;
      }
    })();
    this.buffers.set(url, p);
    return p;
  }

  // --- music transport -----------------------------------------------

  /** Switches playlist, keeping the transport state. '' or an unknown id
   *  degrades to the manifest default. */
  selectPlaylist(id: string): void {
    if (!this.manifest) return;
    const next = this.resolvePlaylist(id);
    if (!next || next === this.playlist) return;
    // A custom Music theme brings its own shuffle switch (themes.md §3.2).
    if (next === this.custom?.list) this.shuffle = this.custom.shuffle;
    this.setPlaylist(next);
  }

  /** The custom playlist built last, so a repeat returns the same object. */
  private custom: { key: string; list: MusicPlaylist; shuffle: boolean } | null = null;

  /**
   * The playlist an id selects now. Out of every mode the custom Music
   * theme comes first, then the keyed gate of the player's own pick. A
   * mode's slot is the manifest's choice and is never gated.
   */
  private resolvePlaylist(id: string): MusicPlaylist | null {
    const m = this.manifest;
    if (!m) return null;
    if (this.mode) return pickPlaylist(m, id);
    return this.customPlaylist() ?? pickPlaylist(m, id, this.settings.held ?? NO_KEYS);
  }

  /** The custom Music theme as a playlist of the manifest's own tracks. */
  private customPlaylist(): MusicPlaylist | null {
    const c = this.settings.customPlaylist;
    const m = this.manifest;
    if (!c || !m) return null;
    const key = customKey(c);
    if (this.custom?.key === key) return this.custom.list;
    const all = m.music.playlists.flatMap((p) => p.tracks);
    const tracks = c.tracks
      .map((url) => all.find((t) => t.url === url) ?? all.find((t) => bareUrl(t.url) === bareUrl(url)))
      .filter((t): t is MusicPlaylist['tracks'][number] => t !== undefined);
    if (tracks.length === 0) return null;
    this.custom = { key, list: { id: c.id, title: c.title, tracks }, shuffle: c.shuffle };
    return this.custom.list;
  }

  /** The SFX theme in force: the custom one, else the gated pick. */
  private pickSfx(): SfxTheme | null {
    if (this.settings.customSfx) return this.settings.customSfx;
    return this.manifest
      ? pickTheme(this.manifest, this.settings.sfxTheme, this.settings.held ?? NO_KEYS)
      : null;
  }

  private setPlaylist(p: MusicPlaylist): void {
    this.playlist = p;
    this.pos = 0;
    // A pending seek belongs to the playlist that armed it. The menu's
    // release to the in-game playlist must not seek into a new track.
    this.pendingSeek = 0;
    this.reorder();
    // A playlist change is a track change, so it crosses like any other
    // (§3.9): hub to world, lobby to match, match to lobby, and a settings
    // playlist swap all reach the player as a fade.
    const d = this.deck();
    if (d) this.goToCursor(d.live);
  }

  /**
   * The playlist the current mode plays.
   *
   * The arcade slot has one step of its own before `pickPlaylist` takes
   * over: arcade, then menu, then the manifest default. The step is the
   * **menu** playlist because the arcade page plays the menu playlist in
   * its lobby — a match with no set of its own must keep the track the
   * player is already hearing (§3.7). An arcade id no playlist carries
   * takes that same step, so a typo in the masters and an unpublished set
   * degrade the same way.
   *
   * Every other id falls straight to `pickPlaylist`, which is the rule the
   * whole manifest obeys: '' and an unknown id both give the default.
   */
  private modePlaylistId(): string {
    const m = this.manifest;
    if (!m) return '';
    if (this.mode === 'arcade') {
      const has = m.music.arcade !== ''
        && m.music.playlists.some((p) => p.id === m.music.arcade);
      return has ? m.music.arcade : m.music.menu;
    }
    if (this.mode === 'menu') return m.music.menu;
    return this.settings.playlist;
  }

  /**
   * Puts the transport in one mode, or takes it out of every mode
   * (audio.md §3.6, §3.7).
   *
   * In a mode the manifest names the playlist and the screen draws no
   * transport for it. Out of every mode the stored playlist comes back and
   * the transport state rides across the swap. The stored `playlist`
   * setting is never written here: a visit to the menu, or a match, must
   * not take the player's own choice.
   *
   * A mode that selects the playlist already running changes nothing, so a
   * host that published no arcade set keeps the lobby's track through the
   * whole match.
   */
  setMusicMode(mode: MusicMode): void {
    if (this.dead || mode === this.mode) return;
    this.mode = mode;
    if (!this.manifest) return;
    this.selectPlaylist(this.modePlaylistId());
    if (mode && this.musicWanted()) this.musicPlay();
  }

  /** Menu mode, which is the landing screen, the hub and the arcade
   *  lobby. It is `setMusicMode` under the name the two menu shells have
   *  always called (R227). */
  menuMusic(on: boolean): void {
    this.setMusicMode(on ? 'menu' : null);
  }

  private selectFromSettings(): void {
    if (!this.manifest) return;
    this.playlist = this.resolvePlaylist(this.modePlaylistId());
    if (this.playlist === this.custom?.list) this.shuffle = this.custom.shuffle;
    this.theme = this.pickSfx();
    this.pos = 0;
    this.reorder();
  }

  private reorder(): void {
    this.order = this.makeOrder(this.playlist?.tracks.length ?? 0, this.order[this.pos] ?? 0);
    this.pos = 0;
  }

  /** The play order over `len` tracks under the shuffle switch: a shuffle
   *  with `first` in front, or the identity. */
  private makeOrder(len: number, first: number): number[] {
    return this.shuffle
      ? makeShuffle(len, first, Math.random)
      : Array.from({ length: len }, (_, i) => i);
  }

  private track(): MusicTrack | null {
    const idx = this.order[this.pos];
    if (idx === undefined) return null;
    return this.playlist?.tracks[idx] ?? null;
  }

  /** Points one deck at the cursor's track. `play` continues transport
   *  across a track change; a fresh selection only arms it. */
  private loadInto(d: Deck, play: boolean): void {
    const t = this.track();
    if (!t) return;
    const el = d.el;
    // A handoff's position, applied once and only where the file gives its
    // duration up. `preload = 'none'` means there is nothing to seek yet.
    // A seek armed for the last track is unhooked first: metadata that has
    // not arrived by the next track change belongs to nothing.
    this.unhookSeek(d);
    el.src = t.url;
    d.url = t.url;
    const seek = this.pendingSeek;
    this.pendingSeek = 0;
    if (seek > 0) {
      const fire = () => {
        d.seek = null;
        try { el.currentTime = seek; } catch { /* not seekable: start at 0 */ }
      };
      d.seek = fire;
      el.addEventListener('loadedmetadata', fire, { once: true });
    }
    if (play) this.start(d);
  }

  /** Drops the armed handoff seek, where one waits on the metadata. */
  private unhookSeek(d: Deck): void {
    if (!d.seek) return;
    d.el.removeEventListener('loadedmetadata', d.seek);
    d.seek = null;
  }

  private start(d: Deck): void {
    d.live = true;
    void d.el.play().catch((e: unknown) => {
      d.live = false; // needs a gesture
      // One line per deck: a refused deck refuses every track, and the name
      // of the error is what tells a policy block from a decode fault.
      if (d.reported) return;
      d.reported = true;
      console.info('[audio] play refused', (e as { name?: string })?.name ?? 'error');
    });
  }

  /** Silences one deck and drops the track it holds. */
  private unload(d: Deck): void {
    d.live = false;
    this.unhookSeek(d);
    // A deck that holds no track needs none of this, and an element with no
    // source is not worth a pause() call.
    if (d.url) {
      d.url = '';
      d.el.pause();
      d.el.removeAttribute('src');
      // Without a `load()` the element keeps the resource it decoded.
      try { d.el.load(); } catch { /* no implementation: nothing to release */ }
    }
    this.setGain(d, 0);
  }

  /** Puts one deck's gain at a value now, dropping any automation on it. */
  private setGain(d: Deck, v: number): void {
    const p = d.gain.gain;
    // From 0, not from now: the call drops the events that START at its time
    // or later, and a crossfade curve that began before now would stay and
    // make the value setter throw "Can't add events during a curve event".
    if (typeof p.cancelScheduledValues === 'function') p.cancelScheduledValues(0);
    try {
      p.value = v;
    } catch {
      // Firefox still threw after the cancel (R1718): a new node holds no curve.
      this.swapGain(d, v);
    }
  }

  /** Gives one deck a new gain node at `v`, in the place of the old one. */
  private swapGain(d: Deck, v: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const old = d.gain;
    const gain = ctx.createGain();
    gain.gain.value = v;
    if (this.musicGain) gain.connect(this.musicGain);
    if (d.source) {
      try { d.source.disconnect(); } catch { /* not connected */ }
      d.source.connect(gain);
    }
    try { old.disconnect(); } catch { /* not connected */ }
    d.gain = gain;
  }

  /**
   * Ends a crossfade at once: the outgoing deck stops and the incoming deck
   * takes full gain. Pause, stop and the music switch land here, because a
   * fade on the way to silence reads as a fault (§3.9).
   */
  private settleFade(): void {
    this.clearFadeTimer();
    const from = this.fadeFrom;
    this.fadeFrom = null;
    if (from) this.unload(from);
    const d = this.deck();
    if (d) this.setGain(d, 1);
  }

  private clearFadeTimer(): void {
    if (this.fadeTimer === null) return;
    clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
  }

  /** Immediate silence on the current deck. Never a fade. */
  private pauseNow(): void {
    this.settleFade();
    const d = this.deck();
    if (!d) return;
    d.live = false;
    if (d.url) d.el.pause();
  }

  /**
   * Moves the transport onto whatever the cursor names now. A deck that is
   * already sounding, and a window above zero, make the change a crossfade;
   * anything else is a hard cut on the one deck (§3.9).
   */
  private goToCursor(play: boolean): void {
    const from = this.deck();
    if (!from) return;
    const ctx = this.ctx;
    const cross = play && this.crossfadeSec > 0 && from.live && from.url !== ''
      && !!ctx && ctx.state === 'running';
    if (cross) {
      this.crossTo(from);
      return;
    }
    this.settleFade();
    this.loadInto(from, play);
  }

  /**
   * Starts the cursor's track on the idle deck and crosses the two gains
   * (§3.9). `from` is the deck that sounds now.
   */
  private crossTo(from: Deck): void {
    const ctx = this.ctx;
    if (!ctx) return;
    // The newest target wins. A fade already in flight left its outgoing
    // deck on the idle slot; that deck stops here, so three sources never
    // sound together, and the deck that was fading IN becomes `from` and
    // fades out from the gain it holds.
    this.clearFadeTimer();
    this.fadeFrom = null;
    const to = this.decks[1 - this.cur];
    this.unload(to);
    const g0 = from.gain.gain.value;
    // The incoming deck is audibly current from here, so the transport card
    // names the new track at the START of the fade (§3.9).
    this.cur = 1 - this.cur;
    this.loadInto(to, true);
    const now = ctx.currentTime;
    // Equal-power: the two curves keep the sum of the squares at 1, so the
    // mix neither clips at the ends nor dips in the middle.
    this.ramp(from, crossfadeCurve(0, g0), now);
    this.ramp(to, crossfadeCurve(1), now);
    this.fadeFrom = from;
    // The gains ride the AudioContext clock; this timer only tidies up
    // after them. A throttled background tab makes it late, and late costs
    // nothing: the curve has already put the outgoing deck at zero.
    this.fadeTimer = setTimeout(() => {
      this.fadeTimer = null;
      this.settleFade();
    }, this.crossfadeSec * 1000);
  }

  /** Schedules one half of a crossfade on the AudioContext clock. */
  private ramp(d: Deck, curve: Float32Array, now: number): void {
    const p = d.gain.gain;
    // From 0, as in setGain: a curve already in flight must go before the new one.
    if (typeof p.cancelScheduledValues === 'function') p.cancelScheduledValues(0);
    const end = curve[curve.length - 1];
    if (typeof p.setValueCurveAtTime !== 'function') {
      this.setGain(d, end);
      return;
    }
    try {
      p.setValueCurveAtTime(curve, now, this.crossfadeSec);
      return;
    } catch { /* the old curve stayed: a new node holds none (R1718) */ }
    this.swapGain(d, curve[0]);
    try {
      d.gain.gain.setValueCurveAtTime(curve, now, this.crossfadeSec);
    } catch {
      this.setGain(d, end);
    }
  }

  /**
   * The crossfade trigger. `timeupdate` fires several times a second while
   * a deck plays, and the last window of the track arms the change (§3.9).
   * A duration the browser does not report, and a track shorter than the
   * window, both fall through to the `ended` fallback.
   */
  private armCrossfade(d: Deck): void {
    if (this.crossfadeSec <= 0 || this.fadeFrom || !d.live || d !== this.deck()) return;
    const dur = d.el.duration;
    if (!isFinite(dur) || dur <= this.crossfadeSec) return;
    if (dur - d.el.currentTime > this.crossfadeSec) return;
    this.advance(1);
  }

  musicPlay(): void {
    this.stopped = false;
    this.pausedByUser = false;
    if (!this.settings.musicOn) return;
    const ctx = this.ensureCtx();
    const d = this.deck();
    if (!ctx || !d) return;
    if (!d.url) this.loadInto(d, false);
    if (!d.url) return;
    // A deck already asked to play is left alone: the boot's autostart and
    // the unlock gesture may both land here (R396), and the second call
    // must not restart the element. A play the browser refused cleared
    // `live`, so a retry still goes through.
    if (!d.live) this.start(d);
  }

  musicPause(): void {
    this.pausedByUser = true;
    this.pauseNow();
  }

  /** Pause and rewind. The stopped flag keeps a settings re-apply or a
   *  later unlock from starting it again behind the player's back. */
  musicStop(): void {
    this.stopped = true;
    this.pausedByUser = true;
    this.pauseNow();
    const d = this.deck();
    if (!d) return;
    try { d.el.currentTime = 0; } catch { /* not seekable yet */ }
  }

  /** Jumps to one track by its place in the playlist. The media-player card
   *  lists the playlist, not the play order, so that is the index it hands
   *  back — under shuffle the cursor moves to wherever the order holds it,
   *  and the rest of the lap is left as it was. */
  musicJump(i: number): void {
    const len = this.playlist?.tracks.length ?? 0;
    if (!Number.isInteger(i) || i < 0 || i >= len) return;
    const at = this.order.indexOf(i);
    if (at < 0) return;
    this.pos = at;
    this.playCursor();
  }

  musicNext(): void {
    this.advance(1);
  }

  musicPrev(): void {
    this.advance(-1);
  }

  /** Reshuffles on the way in so the order is fresh, keeping the track
   *  that is playing first — switching shuffle never cuts a track short. */
  musicShuffle(on: boolean): void {
    if (on === this.shuffle) return;
    this.shuffle = on;
    const len = this.playlist?.tracks.length ?? 0;
    const current = this.order[this.pos] ?? 0;
    this.order = this.makeOrder(len, current);
    this.pos = on ? 0 : Math.min(current, Math.max(0, len - 1));
  }

  /** Moves the cursor and keeps playing. The end of the playlist wraps:
   *  music loops until the player stops it. */
  private advance(dir: number): void {
    const len = this.order.length;
    if (len === 0) return;
    if (this.shuffle && dir > 0 && this.pos === len - 1) {
      // A fresh order for the next lap, starting off the track just heard.
      this.order = makeShuffle(len, this.order[this.pos], Math.random);
      this.pos = nextIndex(0, len);
    } else {
      this.pos = dir > 0 ? nextIndex(this.pos, len) : prevIndex(this.pos, len);
    }
    this.playCursor();
  }

  /** Lifts STOP and PAUSE and moves the transport onto the cursor: the
   *  player asked for this track, so it plays where the switch allows. */
  private playCursor(): void {
    this.stopped = false;
    this.pausedByUser = false;
    this.goToCursor(this.settings.musicOn);
  }

  /**
   * Everything the media-player card draws, read fresh each render. Null
   * where there is no playlist to speak about, which is what hides the
   * card on a host that published SFX only.
   */
  musicStatus(): MusicStatus | null {
    const p = this.playlist;
    if (!p) return null;
    const t = this.track();
    // Across a fade this is the incoming deck, so the card names the new
    // track from the start of the fade and not at its end (§3.9).
    const el = this.deck()?.el ?? null;
    const dur = el && isFinite(el.duration) && el.duration > 0 ? el.duration : (t?.duration ?? 0);
    return {
      playlist: p.id,
      playlistTitle: p.title,
      trackIndex: this.order[this.pos] ?? 0,
      title: t?.title ?? '',
      time: el ? el.currentTime : 0,
      duration: dur,
      playing: !!this.deck()?.live,
      shuffle: this.shuffle,
      tracks: p.tracks.map((x) => ({ title: x.title })),
    };
  }

  /** Overlay teardown reaches here. Everything this director opened —
   *  window listeners, the element, the context — closes with it. */
  destroy(): void {
    this.dead = true;
    this.clearUnlock();
    window.removeEventListener('pagehide', this.onPageHide);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('focus', this.onFocus);
    this.closeGraph();
    this.buffers.clear();
  }
}
